import { BrandLoader } from '@/components/ui/loaders';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, session } from '@/lib/api';
import { announcementFor, newCalls, type BoardCall } from './board-calls';
import { localeDirection } from '@his/shared';
import { useTranslation } from '@/lib/i18n';
import { useForcedDark } from '@/lib/theme';

interface DisplayRow {
  doctorId: string;
  doctorName: string;
  designation: string | null;
  nowServing: string | null;
  waiting: number;
  called?: BoardCall | null;
}

/**
 * Waiting-room board (SRS 6.2, 14.4 "large-font mode for queue displays").
 *
 * Deliberately anonymous: token numbers only, never a patient name, because
 * this screen faces a full waiting room. It needs no sign-in, so a cheap TV
 * stick can point at `/display/{branchId}?tenant=slug` and be left alone.
 */
export function QueueDisplayPage() {
  const { branchId } = useParams<{ branchId: string }>();
  const [params] = useSearchParams();
  const tenant = params.get('tenant');
  const lang = params.get('lang') ?? 'en';
  const { t, locale } = useTranslation('queue', lang);
  const heard = useRef<Set<string>>(new Set());
  const primed = useRef(false);
  useForcedDark();

  useEffect(() => {
    if (tenant) session.setTenant(tenant);
  }, [tenant]);

  const board = useQuery({
    queryKey: ['queue-display', branchId],
    queryFn: () => get<DisplayRow[]>(`/queue/display/${branchId}`),
    refetchInterval: () => (document.visibilityState === 'visible' ? 10_000 : false),
    enabled: Boolean(branchId),
  });

  useEffect(() => {
    if (!board.data) return;
    const calls = board.data.map((row) => row.called).filter((call): call is BoardCall => Boolean(call));
    const fresh = newCalls(calls, heard.current);
    if (!primed.current) {
      primed.current = true;
      return;
    }
    for (const call of fresh) {
      chime();
      speak(announcementFor(call, lang), lang);
    }
  }, [board.data, lang]);

  return (
    <div className="min-h-screen bg-background p-8" lang={locale} dir={localeDirection(locale)}>
      <header className="mb-8 flex items-baseline justify-between">
        <h1 className="text-4xl font-bold tracking-tight">{t('now_serving')}</h1>
        <p className="text-2xl tabular text-muted-foreground">
          {new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' }).format(new Date())}
        </p>
      </header>

      {board.isLoading && <BrandLoader className="py-24" />}

      {board.data && board.data.length === 0 && (
        <p className="text-3xl text-muted-foreground">{t('no_clinics')}</p>
      )}

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {board.data?.map((row) => (
          <div key={row.doctorId} className="rounded-2xl border-2 bg-card p-8">
            <p className="truncate text-2xl font-semibold">{row.doctorName}</p>
            {row.designation && (
              <p className="truncate text-lg text-muted-foreground">{row.designation}</p>
            )}
            <p className="mt-6 text-[6rem] font-black leading-none tabular text-primary">
              {row.nowServing ?? '—'}
            </p>
            {row.called && (
              <p className="mt-4 animate-pulse text-3xl font-bold tabular">
                {t('token')} <bdi dir="ltr">{row.called.tokenNo}</bdi>
                {row.called.room ? ` → ${t('room', { room: row.called.room })}` : ''}
              </p>
            )}
            <p className="mt-4 text-xl text-muted-foreground">
              {t('waiting', { count: row.waiting })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function chime(): void {
  try {
    const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    const context = new AudioCtor();
    const tone = context.createOscillator();
    const gain = context.createGain();
    tone.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.8);
    tone.connect(gain).connect(context.destination);
    tone.start();
    tone.stop(context.currentTime + 0.8);
  } catch {
    return;
  }
}

function speak(text: string, lang: string): void {
  if (!('speechSynthesis' in window)) return;
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang === 'ar' ? 'ar-SA' : lang === 'ur' || lang === 'ur-Latn' ? 'ur-PK' : lang === 'es' ? 'es-ES' : 'en-GB';
  window.speechSynthesis.speak(utterance);
}
